import { RefreshOutlined } from "@mui/icons-material"
import {
  Grid,
  Skeleton,
  Stack,
  Typography,
  Link as MuiLink,
  Container,
  Tabs,
  Tab,
  tabClasses,
  tabsClasses,
  Tooltip,
  Chip,
} from "@mui/material"
import { useStore } from "@nanostores/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"

import { EntityActivity } from "./EntityActivity"
import { EntityFunding } from "./EntityFunding"
import { EntityPortfolio } from "./EntityPortfolio"
import { RebalanceHistory } from "./RebalanceHistory"
import { SingleAgent } from "./SingleAgent"
import { SingleHopperAgent } from "./SingleHopperAgent"
import { SinglePortfolioAgent } from "./SinglePortfolioAgent"
import { Subheading } from "../../components/Subheading"
import { SpawnAgentDialog } from "../HomePage/SpawnAgentDialog"
import { readHopperAgentInfo } from "@/api/agents-api"
import { getMessageById } from "@/api/messages-api"
import { readAgentInfo } from "@/api/portfolio-agents-api"
import { AgentSvg } from "@/components/AgentSvg"
import CloneAgentButton from "@/components/CloneAgentButton"
import { IdBlock } from "@/components/IdBlock"
import { PriceDisplay } from "@/components/PriceDisplay"
import { NumberFont } from "@/components/RootLayout/fonts"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import { HOT_AGENT_PERIOD, HOT_AGENT_REFRESH_INTERVAL, REFRESH_INTERVAL } from "@/settings"
import { $hotAgentsMap } from "@/stores/hot-agents-store"
import { AgentType } from "@/types"
import { isArweaveId } from "@/utils/arweave"
import { truncateId } from "@/utils/data-utils"
import { parseBigIntAsNumber } from "@/utils/format"

const AGENT_TYPE_LABELS: Record<AgentType, string> = {
  portfolio: "Portfolio",
  "dca-order": "DCA Order",
  "limit-order": "Limit Order",
  "stop-order": "Stop Loss Order",
  hopper: "Multi-hop",
}

export function AgentPage() {
  const { entityId } = useParams()

  const { data: message, isFetched } = useQuery({
    queryKey: ["message", entityId],
    queryFn: () => {
      if (!entityId) throw new Error("Invalid params")

      return getMessageById(entityId)
    },
    enabled: !!entityId,
  })

  const [portfolioValue, setPortfolioValue] = useState<number | null>()

  const validAddress = useMemo(() => isArweaveId(String(entityId)), [entityId])

  if (!entityId || !validAddress) {
    return (
      <Stack component="main" gap={4} paddingY={4}>
        <Typography>Invalid Entity ID.</Typography>
      </Stack>
    )
  }

  const agentName = message?.tags["Agent-Name"]

  const networth = useMemo(() => {
    if (typeof portfolioValue !== "number") return null

    return portfolioValue
  }, [portfolioValue])

  const agentType = (
    !message ? null : message.tags["Simple-Agent-Type"] || message.tags["Agent-Type"] || "portfolio"
  ) as AgentType

  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    setActiveTab(0)
  }, [entityId])

  const { data: agentInfo } = useQuery({
    queryKey: ["agent_info", entityId],
    queryFn: async () => {
      if (agentType !== "portfolio") throw new Error("Invalid agent type")
      // console.log("Fetching Agent Info", agent.id)
      const res = await readAgentInfo(entityId)
      console.log("onAgentInfo", entityId, res)
      return res
    },
    enabled: !!entityId && agentType === "portfolio",
  })

  const { data: hopperAgentInfo } = useQuery({
    queryKey: ["agent_info", entityId],
    queryFn: async () => {
      if (agentType !== "hopper") throw new Error("Invalid agent type")
      // console.log("Fetching Agent Info", agent.id)
      const res = await readHopperAgentInfo(entityId)
      console.log("onAgentInfo", entityId, res)
      return res
    },
    enabled: !!entityId && agentType === "hopper",
  })

  const owner =
    message?.tags["Portfolio-Owner"] ||
    message?.tags["Order-Owner"] ||
    hopperAgentInfo?.["Initiator"]

  const [open, setOpen] = React.useState(false)

  const [lastUpdated, setLastUpdated] = useState<number | null>(new Date().getTime())

  const queryClient = useQueryClient()

  const handleForceRefresh = async (force = false) => {
    console.log("onForceRefresh", document.hasFocus())
    if (!document.hasFocus() && !force) return
    queryClient.invalidateQueries({ queryKey: ["agent_info", entityId] })
    queryClient.invalidateQueries({ queryKey: ["token_prices"] })
    queryClient.invalidateQueries({ queryKey: ["existing_balances"] })
    queryClient.invalidateQueries({ queryKey: ["agent_exit_pnl"] })

    setLastUpdated(new Date().getTime())
  }

  const hotAgents = useStore($hotAgentsMap, {
    keys: [entityId],
  })

  useEffect(() => {
    const lastUpdate = Number(hotAgents[entityId])
    const isHot = !isNaN(lastUpdate) && new Date().getTime() - lastUpdate < HOT_AGENT_PERIOD

    const interval = setInterval(
      () => {
        handleForceRefresh(isHot ? true : false)
      },
      isHot ? HOT_AGENT_REFRESH_INTERVAL : REFRESH_INTERVAL,
    )

    return () => clearInterval(interval)
  }, [REFRESH_INTERVAL, entityId, hotAgents])

  return (
    <>
      <Container maxWidth="lg" disableGutters>
        <Stack gap={3}>
          <Stack
            gap={2}
            sx={{
              background: "var(--mui-palette-paperAlt)",
              padding: 2,
              marginX: 0,
              borderRadius: 2,
            }}
          >
            <Grid container spacing={3} sx={{}}>
              <Grid item xs={12} lg={8}>
                <Subheading sx={{ marginX: 0 }}>
                  <Stack direction="row" gap={2} alignItems="center" flexWrap={"wrap"}>
                    {!isFetched ? (
                      <Skeleton variant="circular" width={72} height={72} />
                    ) : (
                      <AgentSvg height={72} width={72} />
                    )}
                    <Stack>
                      <Typography variant="h4">
                        <IdBlock
                          label={agentName || truncateId(entityId)}
                          value={entityId}
                          href={`https://ao.link/#/entity/${entityId}`}
                        />
                      </Typography>
                      {message && (
                        <Typography variant="caption" color="text.secondary">
                          owned by{" "}
                          <MuiLink
                            component={Link}
                            to={`/entity/${owner}`}
                            color="secondary"
                            variant="caption"
                          >
                            {truncateId(owner as string)}
                          </MuiLink>
                        </Typography>
                      )}
                    </Stack>
                    {agentType !== null && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="div"
                        sx={{
                          padding: 0.5,
                          borderRadius: 0.5,
                          background: "rgba(255, 52, 0, 0.1)",
                          color: "rgba(255, 102, 0, 1)",
                        }}
                      >
                        {AGENT_TYPE_LABELS[agentType]} Agent
                      </Typography>
                    )}
                    <Tooltip title={`Last refreshed ${dayjs(lastUpdated).format("HH:MM:ss")}`}>
                      <Chip
                        sx={{
                          borderRadius: 0.5,
                          paddingY: 0.5,
                          height: "fit-content",
                        }}
                        label={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <RefreshOutlined fontSize="small" /> {REFRESH_INTERVAL / 1000}s
                          </Stack>
                        }
                        onClick={() => handleForceRefresh()}
                      />
                    </Tooltip>

                    {agentType === "portfolio" && agentInfo && (
                      <CloneAgentButton
                        onClick={() => {
                          setOpen(true)
                        }}
                      />
                    )}
                  </Stack>
                </Subheading>
              </Grid>

              {agentType !== "hopper" && (
                <Grid item xs={12} lg={4}>
                  <Tooltip
                    title={
                      agentType !== "portfolio"
                        ? null
                        : "Your holdings multiplied by the current token price, reflecting their ideal worth under perfect conditions. It’s your theoretical maximum return."
                    }
                  >
                    <Typography
                      fontFamily={NumberFont}
                      variant="h6"
                      sx={{
                        textTransform: "uppercase",
                      }}
                    >
                      Net worth
                    </Typography>
                  </Tooltip>

                  {agentType === "portfolio" ? (
                    <>
                      {!agentInfo ? (
                        <Skeleton width={100} />
                      ) : (
                        <>
                          <Typography
                            variant="h3"
                            sx={{
                              color: "var(--mui-palette-accent-main)",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                            fontFamily={NumberFont}
                          >
                            <PriceDisplay
                              token={agentInfo["Quote-Token"]}
                              amount={Number(
                                parseBigIntAsNumber(
                                  agentInfo.Holdings?.totalValue,
                                  Number(agentInfo["Quote-Token-Denomination"]),
                                ),
                              )}
                              showOnlyUsd
                            />
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                            fontFamily={NumberFont}
                          >
                            <Tooltip title="Estimated USD if selling your entire token balance in one transaction. This value fluctuates based on real-time liquidity in pools and market volatility, and is not guaranteed.">
                              <span>Realizable: </span>
                            </Tooltip>
                            <PriceDisplay
                              token={agentInfo["Quote-Token"]}
                              amount={Number(
                                parseBigIntAsNumber(
                                  agentInfo.Holdings?.realTotalValue,
                                  Number(agentInfo["Quote-Token-Denomination"]),
                                ),
                              )}
                              showOnlyUsd
                            />
                          </Typography>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {typeof networth !== "number" ? (
                        <Skeleton width={100} />
                      ) : (
                        <Typography
                          variant="h3"
                          sx={{
                            color: "var(--mui-palette-accent-main)",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                          fontFamily={NumberFont}
                        >
                          <UsdAmountBlock amount={networth} />
                        </Typography>
                      )}
                    </>
                  )}
                </Grid>
              )}
            </Grid>

            {agentType === "hopper" ? (
              <SingleHopperAgent entityId={entityId} agentPageView />
            ) : agentType === "portfolio" ? (
              <SinglePortfolioAgent entityId={entityId} />
            ) : (
              <SingleAgent entityId={entityId} />
            )}
          </Stack>
          {!isFetched ? null : (
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                marginX: 0,
                [`& .${tabClasses.root}`]: {
                  marginX: 0,
                  fontSize: "1.4rem",
                  fontWeight: `700 !important`,
                },
                [`& .${tabsClasses.indicator}`]: {
                  background: "var(--mui-palette-accent-main) !important",
                  height: 3,
                },
              }}
            >
              <Tab value={0} label="Overview" />
              <Tab value={1} label="Activity" />
              {owner && <Tab value={2} label="Funding" />}
            </Tabs>
          )}
          <Stack gap={4} sx={{ display: activeTab === 0 ? "flex" : "none" }}>
            <EntityPortfolio
              entityId={entityId}
              onPortfolioValue={setPortfolioValue}
              isPortfolioAgent={agentType === "portfolio"}
              agentInfo={agentInfo}
            />
            {agentType === "portfolio" && <RebalanceHistory entityId={entityId} />}
          </Stack>
          {activeTab === 1 && <EntityActivity entityId={entityId} />}
          {activeTab === 2 && owner && (
            <EntityFunding entityId={entityId} owner={owner as string} />
          )}
        </Stack>
      </Container>
      <SpawnAgentDialog open={open} onClose={() => setOpen(false)} prefill={agentInfo} />
    </>
  )
}
